import React, { FC } from 'react'

import {
  CHIP_TEXTS,
  getDataUnit,
  getDiscountValue,
  getOfferName,
  PLAN_COLLECTION_TAGS,
  pricePresenter,
} from '@smarty-nx/code-utils'
import { getPlanCollectionName, isUnlimitedPlan } from '@smarty-nx/contentful'
import {
  DATA_LAYER_MODULE_TYPES,
  planCardAttributes,
} from '@smarty-nx/tracking'
import cn from 'classnames'

import classes from './plan-card.module.scss'
import { PlanCardProps } from './plan-card.types'
import { getPlanStyles } from '../../utils'
import { Icon } from '../icon/icon'

export const PlanCard: FC<PlanCardProps> = ({
  planData,
  showCollectionTag = false,
  dataItemName = null,
  dataItemPosition,
  onClick,
  imagesBasePath = '',
}) => {
  const {
    id,
    name,
    voucher,
    groups,
    originalPrice,
    finalPrice,
    finalPriceWithoutCredit,
    dataAllowanceGB,
    dataAllowanceWithPromosGB,
    planHasDataDiscount: isDataDiscountPlan,
    planIsDataOnly: isPlanIsDataOnly,
  } = planData

  // TODO: update logic for multiple vouchers when back-end is ready
  // https://smarty-mobile.atlassian.net/browse/DIGDEV-11572
  const hasVoucher = voucher !== null
  const hasGroupDiscount = groups?.groupDiscountPercentage !== null
  const planDataTestId = `${id.toLowerCase().replace(/_/g, '-')}-plan`
  const categoryName = isDataDiscountPlan
    ? 'money-back'
    : isPlanIsDataOnly
    ? 'data-only'
    : 'classic'
  const planCollectionName = getPlanCollectionName(planData?.id)
  const tag = planCollectionName
    ? PLAN_COLLECTION_TAGS[planCollectionName]
    : null
  const isCombinedWithGroups =
    typeof voucher?.isCombinedWithGroups !== 'undefined'
      ? voucher?.isCombinedWithGroups
      : true
  const isMultipleOffers =
    voucher?.promoType === 'multi' ||
    (hasGroupDiscount && voucher && isCombinedWithGroups)
  // Temporary workaround. Hiding ribbon if it's equal to "GROUP DISCOUNT" because we already have group discount banner
  // @TODO: After getting a way to define if a voucher is group discount by some BE response field this can be reworked
  // https://smarty-mobile.atlassian.net/browse/DIGDEV-11572
  const showVoucher =
    hasVoucher && voucher?.offer_chip_text !== CHIP_TEXTS.GROUP_DISCOUNT

  const planStyles = getPlanStyles(id)
  const hasTag = Boolean(showCollectionTag && tag)

  return (
    <button
      id={id}
      className={cn(classes.cardWrapper, {
        [classes.hasTag]: hasTag,
      })}
      data-testid={planDataTestId}
      data-item-id={dataItemName}
      data-position={dataItemPosition}
      data-type={DATA_LAYER_MODULE_TYPES.PRODUCT}
      data-asset_discount_coupon={getOfferName(planData)}
      data-asset_discount_value_display-gross={getDiscountValue(planData)}
      onClick={onClick}
      style={
        {
          '--plan-color': planStyles.planColor,
          '--plan-color-pale': planStyles.planColorPale,
        } as React.CSSProperties
      }
      {...planCardAttributes(planData)}
    >
      <div className={classes.contentWrapper}>
        {hasTag && <div className={classes.tagWrapper}>{tag}</div>}

        {isUnlimitedPlan(id) ? (
          <p
            className={classes.unlimitedTitle}
            data-testid={`${categoryName}-data-unlimited`}
          >
            {name}
          </p>
        ) : (
          <div className={classes.amountAndPriceWrapper}>
            {!hasVoucher && !hasGroupDiscount ? (
              <>
                <div
                  className={classes.planData}
                  data-testid={`${categoryName}-data-amount`}
                >
                  {dataAllowanceGB}
                </div>
                <div
                  className={classes.planDataUnit}
                  data-testid={`${categoryName}-data-unit`}
                >
                  {getDataUnit(dataAllowanceGB)}
                </div>
              </>
            ) : (
              <>
                <div
                  className={classes.planData}
                  data-testid={`${categoryName}-data-amount`}
                >
                  {dataAllowanceWithPromosGB}
                </div>
                <div
                  className={classes.planDataUnit}
                  data-testid={`${categoryName}-data-unit`}
                >
                  {getDataUnit(dataAllowanceWithPromosGB)}
                </div>
                {dataAllowanceGB !== dataAllowanceWithPromosGB && (
                  <div
                    className={classes.strikeThrough}
                    data-testid={`${categoryName}-data-promo-amount`}
                  >
                    {dataAllowanceGB}
                    {getDataUnit(dataAllowanceGB)}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div
          className={classes.amountAndPriceWrapper}
          data-testid={`${categoryName}-price`}
        >
          {!hasVoucher && !hasGroupDiscount ? (
            <div className={classes.planPrice} data-testid="original-price">
              {pricePresenter(originalPrice.value)}
            </div>
          ) : (
            <>
              <div className={classes.planPrice} data-testid="final-price">
                {pricePresenter(finalPriceWithoutCredit.value)}
              </div>
              {originalPrice.value !== finalPrice.value && (
                <div
                  className={classes.strikeThrough}
                  data-testid="original-price"
                >
                  {pricePresenter(originalPrice.value)}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {isDataDiscountPlan && (
        <div
          className={cn(classes.voucherWrapper, classes.bottomInfo)}
          data-testid={`${id}-voucher-wrapper`}
        >
          <img src={`${imagesBasePath}/general/coin-promo.svg`} alt="promo" />
          Up to £{dataAllowanceGB} back
        </div>
      )}

      {showVoucher && (
        <div
          className={cn(
            classes.voucherWrapper,
            classes.voucher,
            classes.bottomInfo
          )}
          data-testid={`${id}-voucher-wrapper`}
        >
          {isMultipleOffers ? (
            <>
              <Icon
                name="multiple_offers"
                aria-label="Multiple offers"
                className={classes.multipleOffersIcon}
              />
              Multiple offers
            </>
          ) : (
            <>
              <Icon
                name="tag"
                aria-label={voucher?.offer_chip_text}
                className={classes.tagIcon}
              />
              {voucher?.offer_chip_text}
            </>
          )}
        </div>
      )}
    </button>
  )
}
